import { getGSAPSettings } from '../utils/GSAP';

const checkTypewriterElements = document.querySelectorAll('[vdx-gsap-typewriter]');

if (checkTypewriterElements.length > 0){    
    if (typeof gsap === 'undefined') {
        console.log('🚨 GSAP is missing! Please turn on GSAP in Webflow for the magic to happen! ✨');
    }   
    if (typeof SplitText === 'undefined') {
        console.log('📝 Oops! GSAP SplitText plugin is required for cool text animations! Enable it in Webflow settings! 🎭');
    }
    if (typeof ScrollTrigger === 'undefined') {
        console.log('📜 ScrollTrigger not found! Your animations need this awesome plugin - activate it in Webflow! 🔥');
    }   
}

if (checkTypewriterElements.length > 0 && typeof gsap !== 'undefined' && typeof SplitText !== 'undefined' && typeof ScrollTrigger !== 'undefined'){
    gsap.registerPlugin(SplitText, ScrollTrigger);
}

export const typewriter = () => {
    if (checkTypewriterElements.length > 0 && typeof gsap !== 'undefined' && typeof SplitText !== 'undefined' && typeof ScrollTrigger !== 'undefined'){
        const typewriterElements = gsap.utils.toArray('[vdx-gsap-typewriter]');
        if (typewriterElements.length === 0) {
            return;
    }
    
    // Create a single IntersectionObserver for all elements
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                // Get the element and animate it
                animateElement(entry.target);
                // Unobserve after animation is triggered
                observer.unobserve(entry.target);
            }
        });
    }, {
        threshold: 0.1 // Trigger when 10% of the element is visible
    });
    
    // Function to handle the animation of an element
    const animateElement = (el) => {
        // Make element visible when in view
        gsap.set(el, { 
            opacity: 1,
            visibility: 'visible'
        });
        
        // Get the animation type from the attribute
        const animationType = el.getAttribute('vdx-gsap-typewriter');
        const { duration, stagger, ease, disableBreakpoint } = getGSAPSettings(el);

        // Check if animations should be disabled based on breakpoint
        if (disableBreakpoint > 0 && window.innerWidth <= disableBreakpoint) {
            // Make all children visible without animation
            gsap.set(el.children, { opacity: 1 });
            return;
        }

        // Create split text for this element
        const split = new SplitText(el, {
            type: 'chars,words',
        });
        
        // Apply the appropriate animation based on the attribute value
        switch(animationType) {
            case 'fade':
                // Simple fade in of characters
                gsap.from(split.chars, {
                    opacity: 0,
                    duration: duration,
                    stagger: stagger,
                    ease: ease
                });
                break;
                
            case 'slide-up':
                // Characters slide up into place
                gsap.from(split.chars, {
                    y: 20,
                    opacity: 0,
                    duration: duration,
                    stagger: stagger,
                    ease: ease
                });
                break;
                
            case 'slide-down':
                // Characters slide down into place
                gsap.from(split.chars, {
                    y: -20,
                    opacity: 0,
                    duration: duration,
                    stagger: stagger,
                    ease: ease
                });
                break;
                
            case 'slide-left':
                // Characters slide in from the right
                gsap.from(split.chars, {
                    x: 20,
                    opacity: 0,
                    duration: duration,
                    stagger: stagger,
                    ease: ease
                });
                break;
                
            case 'slide-right':
                // Characters slide in from the left
                gsap.from(split.chars, {
                    x: -20,
                    opacity: 0,
                    duration: duration,
                    stagger: stagger,
                    ease: ease
                });
                break;
                
            case 'scale':
                // Characters scale up from nothing
                gsap.from(split.chars, {
                    scale: 0,
                    opacity: 0,
                    duration: duration,
                    stagger: stagger,
                    ease: ease
                });
                break;
                
            case 'rotate':
                // Characters rotate in
                gsap.from(split.chars, {
                    rotation: 180,
                    opacity: 0,
                    duration: duration,
                    stagger: stagger,
                    ease: ease
                });
                break;
                
            case 'bounce':
                // Characters bounce in
                gsap.from(split.chars, {
                    y: 20,
                    opacity: 0,
                    duration: duration * 2,
                    stagger: stagger,
                    ease: "bounce.out"
                });
                break;
                
            case 'wave':
                // Characters wave in with a sine motion
                gsap.from(split.chars, {
                    y: 10,
                    opacity: 0,
                    duration: duration * 2,
                    stagger: stagger,
                    ease: "sine.inOut",
                    yoyo: true,
                    repeat: 1
                });
                break;
                
            case 'random':
                // Characters appear from random directions
                split.chars.forEach(char => {
                    const randomX = (Math.random() - 0.5) * 40;
                    const randomY = (Math.random() - 0.5) * 40;
                    const randomRotation = (Math.random() - 0.5) * 90;
                    
                    gsap.from(char, {
                        x: randomX,
                        y: randomY,
                        rotation: randomRotation,
                        opacity: 0,
                        duration: duration * 1.5,
                        delay: Math.random() * (stagger * split.chars.length),
                        ease: ease
                    });
                });
                break;
                
            case 'classic':
                // Classic typewriter effect with cursor
                // First, hide all characters
                gsap.set(split.chars, { opacity: 0 });
                
                // Create a cursor element
                const cursor = document.createElement('span');
                cursor.innerHTML = '|';
                cursor.style.display = 'inline-block';
                cursor.style.marginLeft = '2px';
                cursor.style.fontWeight = 'bold';
                
                // Insert cursor at the beginning
                if (split.chars.length > 0) {
                    split.chars[0].parentNode.insertBefore(cursor, split.chars[0]);
                } else {
                    el.appendChild(cursor);
                }
                
                // Animate cursor blinking
                gsap.to(cursor, {
                    opacity: 0,
                    repeat: -1,
                    yoyo: true,
                    duration: 0.5
                });
                
                // Create timeline for character reveal and cursor movement
                const typingTimeline = gsap.timeline();
                
                // Reveal characters one by one and move cursor
                split.chars.forEach((char, index) => {
                    typingTimeline.to(char, {
                        opacity: 1,
                        duration: 0.01,
                        ease: "none",
                        onComplete: () => {
                            // Move cursor after the current character
                            if (index < split.chars.length - 1) {
                                split.chars[index + 1].parentNode.insertBefore(cursor, split.chars[index + 1]);
                            } else {
                                // After the last character
                                split.chars[index].parentNode.appendChild(cursor);
                            }
                        }
                    }, index * (stagger * 2) + Math.random() * 0.08);
                });
                break;

                case 'digital':
                    // Digital/computer terminal style typewriter effect with cursor
                    // First, hide all characters
                    gsap.set(split.chars, { opacity: 0 });
                    
                    // Create a cursor element
                    const digitalCursor = document.createElement('span');
                    digitalCursor.innerHTML = '█';
                    digitalCursor.style.display = 'inline-block';
                    digitalCursor.style.marginLeft = '2px';
                    digitalCursor.style.fontWeight = 'bold';
                    
                    // Insert cursor at the beginning
                    if (split.chars.length > 0) {
                        split.chars[0].parentNode.insertBefore(digitalCursor, split.chars[0]);
                    } else {
                        el.appendChild(digitalCursor);
                    }
                    
                    // Animate cursor blinking
                    gsap.to(digitalCursor, {
                        opacity: 0,
                        repeat: -1,
                        yoyo: true,
                        duration: 0.5
                    });
                    
                    // Create timeline for character reveal and cursor movement
                    const digitalTypingTimeline = gsap.timeline();
                    
                    // Reveal characters one by one and move cursor
                    split.chars.forEach((char, index) => {
                        digitalTypingTimeline.to(char, {
                            opacity: 1,
                            duration: 0.01,
                            ease: "none",
                            onComplete: () => {
                                // Move cursor after the current character
                                if (index < split.chars.length - 1) {
                                    split.chars[index + 1].parentNode.insertBefore(digitalCursor, split.chars[index + 1]);
                                } else {
                                    // After the last character
                                    split.chars[index].parentNode.appendChild(digitalCursor);
                                }
                            }
                    }, index * (stagger * 2) + Math.random() * 0.08);
                });
                break;
                
            default:
                // Default typewriter effect (original behavior)
                gsap.to(split.chars, {
                    duration: duration,
                    y: 10,
                    ease: ease,
                    stagger: stagger,
                });
                break;
        }
    };
    
    // Process each element individually to prepare for animation
    typewriterElements.forEach(el => {
        // Initially hide the element
        gsap.set(el, { 
            opacity: 0,
            visibility: 'hidden'
        });
        
        // Start observing the element
        observer.observe(el);
    });
    }
}